import React, { useState, useEffect, useRef } from "react";
import { FaUpload, FaTimes, FaFile, FaUsers, FaTags, FaBuilding, FaCalendarAlt, FaFilePdf, FaGlobe, FaInfoCircle, FaExclamationTriangle } from "react-icons/fa";
import api from "../utils/api";

/** Must match server: titles with 8+ words are checked for duplicates */
const TITLE_DUP_CHECK_MIN_WORDS = 8;

function titleWordCount(title) {
  const n = String(title || "").trim().replace(/\s+/g, " ");
  if (!n) return 0;
  return n.split(" ").filter(Boolean).length;
}

export default function UploadResearchModal({ isOpen, onClose, onResearchAdded }) {
  const [formData, setFormData] = useState({
    title: "",
    authors: "",
    abstract: "",
    keywords: "",
    department: "",
    year: new Date().getFullYear(),
    isPublic: false
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [titleDuplicate, setTitleDuplicate] = useState(false);
  const [duplicateExistingTitle, setDuplicateExistingTitle] = useState("");
  const titleCheckTimer = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const words = titleWordCount(formData.title);
    if (words < TITLE_DUP_CHECK_MIN_WORDS) {
      setTitleDuplicate(false);
      setDuplicateExistingTitle("");
      return;
    }

    if (titleCheckTimer.current) clearTimeout(titleCheckTimer.current);
    titleCheckTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/research/check-title-duplicate", {
          params: { title: formData.title }
        });
        if (data.duplicate) {
          setTitleDuplicate(true);
          setDuplicateExistingTitle(data.existingTitle || "");
        } else {
          setTitleDuplicate(false);
          setDuplicateExistingTitle("");
        }
      } catch {
        setTitleDuplicate(false);
        setDuplicateExistingTitle("");
      }
    }, 450);

    return () => {
      if (titleCheckTimer.current) clearTimeout(titleCheckTimer.current);
    };
  }, [formData.title, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a PDF file");
      return;
    }

    if (titleWordCount(formData.title) >= TITLE_DUP_CHECK_MIN_WORDS && titleDuplicate) {
      setError(
        "This title matches existing research (duplicate check applies when the title has 8 or more words). Change the title to continue."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = new FormData();
      data.append("pdf", file);
      data.append("title", formData.title);
      data.append("authors", formData.authors);
      data.append("abstract", formData.abstract);
      data.append("keywords", formData.keywords);
      data.append("department", formData.department);
      data.append("year", formData.year);
      data.append("isPublic", formData.isPublic);

      await api.post("/research", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      onResearchAdded();
      onClose();
      setFormData({
        title: "",
        authors: "",
        abstract: "",
        keywords: "",
        department: "",
        year: new Date().getFullYear(),
        isPublic: false
      });
      setFile(null);
    } catch (error) {
      setError(error.response?.data?.error || "Failed to upload research");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setError("");
    } else {
      setError("Please select a valid PDF file");
      setFile(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="relative bg-gray-900 p-8 rounded-2xl shadow-2xl w-full max-w-2xl mx-2 max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white text-2xl font-bold focus:outline-none group"
          aria-label="Close"
        >
          <FaTimes className="group-hover:scale-110 transition-transform" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FaUpload className="text-green-500 text-2xl" />
          <h2 className="text-2xl font-bold text-white">Upload Research Document</h2>
        </div>
        <p className="text-sm text-gray-400 mb-4 -mt-2">
          When DrillBit is configured on the server, uploaded PDFs are sent for plagiarism analysis automatically.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaFile className="text-blue-400" />
              Research Title *
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Enter the complete research title
                </div>
              </div>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                setError("");
              }}
              className={`w-full p-3 rounded-lg bg-gray-800 text-white border focus:ring-2 focus:ring-opacity-50 transition-all ${
                titleDuplicate
                  ? "border-amber-500 focus:border-amber-500 focus:ring-amber-500"
                  : "border-gray-600 focus:border-blue-500 focus:ring-blue-500"
              }`}
              required
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Titles with <span className="text-gray-400 font-medium">8 or more words</span> are checked for duplicates (same title, ignoring extra spaces and letter case).
            </p>
            {titleWordCount(formData.title) >= TITLE_DUP_CHECK_MIN_WORDS && titleDuplicate && (
              <div
                role="alert"
                className="mt-2 flex gap-2 items-start p-3 rounded-lg bg-amber-950/80 border-2 border-amber-500 text-amber-50 text-sm shadow-lg shadow-amber-900/20"
              >
                <FaExclamationTriangle className="mt-0.5 shrink-0 text-amber-400 text-lg" aria-hidden />
                <div>
                  <p className="font-semibold text-amber-200">Warning: duplicate title</p>
                  <p className="mt-1 text-amber-100/95">
                    A paper with this title already exists
                    {duplicateExistingTitle ? (
                      <>
                        : <span className="font-medium text-white">&ldquo;{duplicateExistingTitle}&rdquo;</span>
                      </>
                    ) : (
                      "."
                    )}{" "}
                    Change the title before uploading, or the upload will be rejected.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaUsers className="text-blue-400" />
              Authors *
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Separate multiple authors with commas
                </div>
              </div>
            </label>
            <input
              type="text"
              value={formData.authors}
              onChange={(e) => setFormData({...formData, authors: e.target.value})}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              placeholder="Author 1, Author 2, Author 3"
              required
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaFile className="text-blue-400" />
              Abstract *
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Brief summary of the research
                </div>
              </div>
            </label>
            <textarea
              value={formData.abstract}
              onChange={(e) => setFormData({...formData, abstract: e.target.value})}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all h-24 resize-none"
              required
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaTags className="text-blue-400" />
              Keywords
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Separate keywords with commas
                </div>
              </div>
            </label>
            <input
              type="text"
              value={formData.keywords}
              onChange={(e) => setFormData({...formData, keywords: e.target.value})}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              placeholder="keyword1, keyword2, keyword3"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaBuilding className="text-blue-400" />
              Department
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Department or faculty name
                </div>
              </div>
            </label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => setFormData({...formData, department: e.target.value})}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaCalendarAlt className="text-blue-400" />
              Year *
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Year the research was completed
                </div>
              </div>
            </label>
            <input
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({...formData, year: e.target.value})}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              min="1900"
              max={new Date().getFullYear() + 1}
              required
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaFilePdf className="text-red-400" />
              PDF File *
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Upload the research document in PDF format
                </div>
              </div>
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700"
              required
            />
            {file && (
              <p className="text-sm text-green-400 mt-2 flex items-center gap-2">
                <FaFilePdf className="text-red-400" />
                Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-800 rounded-lg border border-gray-600">
            <input
              type="checkbox"
              id="isPublic"
              checked={formData.isPublic}
              onChange={(e) => setFormData({...formData, isPublic: e.target.checked})}
              className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="isPublic" className="flex items-center gap-2 text-sm text-gray-300">
              <FaGlobe className="text-green-400" />
              Make this research publicly available
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Allow students to access without approval
                </div>
              </div>
            </label>
          </div>

          {error && (
            <div className="text-red-400 text-sm p-3 bg-red-900 bg-opacity-50 rounded-lg border border-red-500">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-800 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <FaUpload />
                  Upload Research
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              <FaTimes />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 